import React from "react";
import type { SidebarTabId } from "../types";
import type { LucideIcon } from "lucide-react";
import {
  Map,
  BookOpen,
  BookMarked,
  UserRound,
  Bell,
  Clock,
  Settings,
} from "lucide-react";
import { useI18n } from "../i18n";
import type { TranslationKeys } from "../i18n";
import { BadgeWrapper } from "./BadgeWrapper";

interface SidebarTabsProps {
  activeTab: SidebarTabId;
  onTabChange: (tab: SidebarTabId) => void;
  collapsed: boolean;
  isGM?: boolean;
  hasSession?: boolean;
  onExpandWithTab?: (tab: SidebarTabId) => void;
  onDetach?: (tabId: SidebarTabId) => void;
  isDetached?: (tabId: SidebarTabId) => boolean;
  notificationTabs?: SidebarTabId[];
  onSettingsOpen?: () => void;
}

const allTabsDef: {
  id: SidebarTabId;
  Icon: LucideIcon;
  labelKey: keyof TranslationKeys;
}[] = [
  { id: "map", Icon: Map, labelKey: "sidebar.map" },
  { id: "journal", Icon: BookOpen, labelKey: "sidebar.journal" },
  { id: "characters", Icon: UserRound, labelKey: "sidebar.characters" },
  { id: "srd", Icon: BookMarked, labelKey: "sidebar.srd" },
  { id: "alerts", Icon: Bell, labelKey: "sidebar.alerts" },
  { id: "timeline", Icon: Clock, labelKey: "sidebar.timeline" },
];

/** Exported for backward compatibility */
export const allTabs = allTabsDef;

export function getTabLabel(tabId: SidebarTabId): string {
  // This function is used outside React components, so it returns the key ID
  // For translated labels, use useI18n().t() directly in components
  const tab = allTabsDef.find((t) => t.id === tabId);
  return tab?.labelKey ?? tabId;
}

const SidebarTabs = React.memo(function SidebarTabs({
  activeTab,
  onTabChange,
  collapsed = false,
  isGM,
  hasSession,
  onExpandWithTab,
  onDetach,
  isDetached,
  notificationTabs,
  onSettingsOpen,
}: SidebarTabsProps) {
  const { t } = useI18n();

  // Filter conditional tabs
  const tabs = allTabsDef.filter((tab) => {
    if (tab.id === "alerts") return isGM && hasSession;
    if (tab.id === "timeline") return hasSession;
    return true;
  });

  const handleTabClick = (tabId: SidebarTabId) => {
    if (collapsed && onExpandWithTab) {
      onExpandWithTab(tabId);
    } else {
      onTabChange(tabId);
    }
  };

  return (
    <div className="sidebar-icon-strip-wrapper">
      <div className="sidebar-icon-strip" role="tablist">
        {tabs.map((tab) => {
          const label = t(tab.labelKey);
          return (
            <button
              key={tab.id}
              role="tab"
              data-testid={`sidebar-tab-${tab.id}`}
              className={`sidebar-icon-strip__item${activeTab === tab.id ? " sidebar-icon-strip__item--active" : ""}${isDetached?.(tab.id) ? " sidebar-icon-strip__item--detached" : ""}`}
              onClick={() => handleTabClick(tab.id)}
              title={
                isDetached?.(tab.id)
                  ? `${label} (${t("sidebar.detached")})`
                  : label
              }
              aria-label={label}
              aria-selected={activeTab === tab.id}
            >
              <BadgeWrapper featureId={`sidebar:${tab.id}`}>
                <span className="sidebar-icon-strip__icon">
                  <tab.Icon size={18} />
                </span>
              </BadgeWrapper>
              {notificationTabs?.includes(tab.id) && (
                <span
                  className="absolute top-0.5 right-0.5 w-2 h-2 bg-red-500 rounded-full"
                  aria-label={t("sidebar.notification")}
                />
              )}
            </button>
          );
        })}
      </div>
      {onSettingsOpen && (
        <div className="sidebar-icon-strip sidebar-icon-strip--bottom">
          <button
            className="sidebar-icon-strip__item"
            onClick={onSettingsOpen}
            title={t("settings.title")}
            aria-label={t("settings.title")}
            data-testid="sidebar-settings"
          >
            <span className="sidebar-icon-strip__icon">
              <Settings size={18} />
            </span>
          </button>
        </div>
      )}
    </div>
  );
});

export default SidebarTabs;
